import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  Stack,
  Chip,
  Button,
  Skeleton,
  InputAdornment,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  getAllExchangeRates,
  addExchangeRate,
  deleteExchangeRate,
  getCurrencies,
} from "../../api/Currency/exchangeRateApi";

import { SidebarContext } from "../Sidebar/SidebarProvider";

import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";

import {
  pageHeroShellSx,
  pageContentCardSx,
  pageFieldSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageMobileCardSx,
  pageSectionTitleSx,
  pageDataGridSx,
} from "../../styles/pageModernStyles";

const EMPTY_RATE = {
  currency_from: "",
  currency_to: "",
  rate: "",
  date: "",
};

const CurrencyManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [exchangeRates, setExchangeRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRate, setNewRate] = useState(EMPTY_RATE);
  const [editingRateId, setEditingRateId] = useState(null);
  const [formError, setFormError] = useState(false);
  const [search, setSearch] = useState("");

  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 1)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      minWidth: 0,
      flex: 1,
      width: "100%",
    }),
    [theme],
  );

  const selectFormSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      flex: 1,
      minWidth: { xs: "100%", sm: 140 },
      width: { xs: "100%", sm: "auto" },
    }),
    [theme],
  );

  const loadRates = useCallback(async () => {
    const updatedRates = await getAllExchangeRates();
    setExchangeRates(updatedRates);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [rates, currenciesList] = await Promise.all([
          getAllExchangeRates(),
          getCurrencies(),
        ]);
        setExchangeRates(rates);
        setCurrencies(currenciesList);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const stats = useMemo(() => {
    const pairs = new Set(
      exchangeRates.map((r) => `${r.currency_from}-${r.currency_to}`),
    );
    return {
      total: exchangeRates.length,
      pairs: pairs.size,
      currencies: currencies.length,
    };
  }, [exchangeRates, currencies.length]);

  const filteredRates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return exchangeRates;
    return exchangeRates.filter((r) => {
      const haystack = [
        r.currency_from,
        r.currency_to,
        String(r.rate),
        r.date,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [exchangeRates, search]);

  const handleCreateOrUpdateRate = async () => {
    const { currency_from, currency_to, rate, date } = newRate;
    if (!currency_from || !currency_to || !rate || !date) {
      setFormError(true);
      return;
    }
    setFormError(false);
    setSaving(true);
    try {
      await addExchangeRate(currency_from, currency_to, parseFloat(rate), date);
      await loadRates();
      setNewRate(EMPTY_RATE);
      setEditingRateId(null);
    } catch (error) {
      console.error("Error creando tasa:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditRate = (rate) => {
    setFormError(false);
    setNewRate({
      currency_from: rate.currency_from,
      currency_to: rate.currency_to,
      rate: rate.rate,
      date: rate.date,
    });
    setEditingRateId(rate.id);
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCancelEdit = () => {
    setFormError(false);
    setNewRate(EMPTY_RATE);
    setEditingRateId(null);
  };

  const handleDeleteRate = async (id) => {
    try {
      await deleteExchangeRate(id);
      if (editingRateId === id) {
        handleCancelEdit();
      }
      await loadRates();
    } catch (error) {
      console.error("Error eliminando tasa de cambio:", error);
    }
  };

  const getRowId = useCallback((row) => {
    if (row.id != null) return String(row.id);
    return `${row.currency_from}-${row.currency_to}-${row.date}`;
  }, []);

  const gridColumns = useMemo(
    () => [
      {
        field: "currency_from",
        headerName: t("CurrencyManagerPage.from"),
        width: 110,
        flex: 0.6,
        minWidth: 90,
      },
      {
        field: "currency_to",
        headerName: t("CurrencyManagerPage.to"),
        width: 110,
        flex: 0.6,
        minWidth: 90,
      },
      {
        field: "rate",
        headerName: t("CurrencyManagerPage.rate"),
        width: 130,
        flex: 0.7,
        minWidth: 100,
      },
      {
        field: "date",
        headerName: t("CurrencyManagerPage.date"),
        width: 140,
        flex: 0.7,
        minWidth: 110,
      },
      {
        field: "actions",
        headerName: t("CurrencyManagerPage.actions"),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title={t("CurrencyManagerPage.edit")}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleEditRate(params.row)}
                  aria-label={t("CurrencyManagerPage.edit")}
                  sx={{
                    border: 1,
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                  }}
                >
                  <ModeEditOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("CurrencyManagerPage.delete")}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteRate(params.row.id)}
                  aria-label={t("CurrencyManagerPage.delete")}
                  sx={{
                    border: 1,
                    borderColor: alpha(theme.palette.error.main, 0.35),
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, theme],
  );

  const renderMobileRateCard = (rate) => {
    const isEditing = editingRateId === rate.id;
    return (
      <Box
        key={getRowId(rate)}
        sx={pageMobileCardSx(theme, {
          active: isEditing,
          accentColor: isEditing ? accent : theme.palette.info.main,
        })}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Chip
              label={rate.currency_from}
              size="small"
              sx={{ fontWeight: 800, height: 24 }}
            />
            <ArrowForwardOutlinedIcon
              sx={{ fontSize: 16, color: "text.secondary" }}
            />
            <Chip
              label={rate.currency_to}
              size="small"
              sx={{ fontWeight: 800, height: 24 }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {rate.date}
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.1rem",
              color: theme.palette.primary.dark,
            }}
          >
            {rate.rate}
          </Typography>

          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ModeEditOutlineOutlinedIcon />}
              onClick={() => handleEditRate(rate)}
            >
              {t("CurrencyManagerPage.edit")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              onClick={() => handleDeleteRate(rate.id)}
            >
              {t("CurrencyManagerPage.delete")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderForm = () => (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        useFlexGap
        flexWrap="wrap"
      >
        <FormControl size="small" sx={selectFormSx}>
          <InputLabel id="currency-from-label">
            {t("CurrencyManagerPage.from")}
          </InputLabel>
          <Select
            labelId="currency-from-label"
            label={t("CurrencyManagerPage.from")}
            value={newRate.currency_from}
            variant="outlined"
            onChange={(e) => {
              setFormError(false);
              setNewRate({ ...newRate, currency_from: e.target.value });
            }}
          >
            <MenuItem value="">
              <em>—</em>
            </MenuItem>
            {currencies.map((currency) => (
              <MenuItem key={currency.id} value={currency.code}>
                {currency.code} — {currency.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={selectFormSx}>
          <InputLabel id="currency-to-label">
            {t("CurrencyManagerPage.to")}
          </InputLabel>
          <Select
            labelId="currency-to-label"
            label={t("CurrencyManagerPage.to")}
            value={newRate.currency_to}
            variant="outlined"
            onChange={(e) => {
              setFormError(false);
              setNewRate({ ...newRate, currency_to: e.target.value });
            }}
          >
            <MenuItem value="">
              <em>—</em>
            </MenuItem>
            {currencies.map((currency) => (
              <MenuItem key={currency.id} value={currency.code}>
                {currency.code} — {currency.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          variant="outlined"
          label={t("CurrencyManagerPage.rate")}
          type="number"
          value={newRate.rate}
          onChange={(e) => {
            setFormError(false);
            setNewRate({ ...newRate, rate: e.target.value });
          }}
          sx={fieldSx}
        />

        <TextField
          size="small"
          variant="outlined"
          label={t("CurrencyManagerPage.date")}
          type="date"
          value={newRate.date}
          onChange={(e) => {
            setFormError(false);
            setNewRate({ ...newRate, date: e.target.value });
          }}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          variant="contained"
          onClick={handleCreateOrUpdateRate}
          disabled={saving}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : editingRateId ? (
              <ModeEditOutlineOutlinedIcon />
            ) : (
              <AddOutlinedIcon />
            )
          }
          sx={{
            ...pagePrimaryButtonSx(theme),
            px: 2.5,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {editingRateId
            ? t("CurrencyManagerPage.saveRate")
            : t("CurrencyManagerPage.addRate")}
        </Button>
        {editingRateId ? (
          <Button
            variant="outlined"
            onClick={handleCancelEdit}
            startIcon={<CloseOutlinedIcon />}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {t("CurrencyManagerPage.cancelEdit", { defaultValue: "Cancelar" })}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        ...contentMargin,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1040,
          mx: "auto",
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Hero */}
        <Box sx={{ ...pageHeroShellSx(theme, { sticky: !isMobile }), mb: 1.5 }}>
          <Stack spacing={{ xs: 1.25, md: 1 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              spacing={{ xs: 1.25, md: 1 }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box
                  sx={{
                    width: { xs: 44, md: 40 },
                    height: { xs: 44, md: 40 },
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(accent, 0.14),
                    color: theme.palette.primary.dark,
                  }}
                >
                  <CurrencyExchangeOutlinedIcon
                    sx={{ fontSize: { xs: 22, md: 20 } }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      fontSize: { xs: "1.05rem", md: "1.12rem" },
                    }}
                  >
                    {t("sidebar.currencyManager")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.35,
                      display: { xs: "block", md: "none" },
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {t("CurrencyManagerPage.heroSubtitle")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 0.5, rowGap: 0.35 }}
                  >
                    <Chip
                      label={`${stats.total} ${t("CurrencyManagerPage.ratesCount", { defaultValue: "tasas" })}`}
                      size="small"
                      variant="outlined"
                      sx={pageKpiChipSx}
                    />
                    <Chip
                      label={`${stats.pairs} ${t("CurrencyManagerPage.pairsCount", { defaultValue: "pares" })}`}
                      size="small"
                      sx={{
                        ...pageKpiChipSx,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.dark,
                      }}
                    />
                    {stats.currencies > 0 ? (
                      <Chip
                        label={`${stats.currencies} ${t("CurrencyManagerPage.currenciesCount", { defaultValue: "monedas" })}`}
                        size="small"
                        sx={{
                          ...pageKpiChipSx,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.dark,
                        }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              <TextField
                size="small"
                placeholder={t("CurrencyManagerPage.searchPlaceholder", {
                  defaultValue: "Buscar moneda, tasa o fecha…",
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", md: 280 },
                  flexShrink: 0,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </Box>

        {formError ? (
          <Alert
            severity="warning"
            sx={{ mb: 1.5, borderRadius: 2 }}
            onClose={() => setFormError(false)}
          >
            {t("CurrencyManagerPage.requiredFields")}
          </Alert>
        ) : null}

        {editingRateId ? (
          <Alert
            severity="info"
            sx={{ mb: 1.5, borderRadius: 2 }}
            onClose={handleCancelEdit}
          >
            {t("CurrencyManagerPage.editingHint", {
              defaultValue: "Editando una tasa existente. Guarda o cancela.",
            })}
          </Alert>
        ) : null}

        {/* Formulario */}
        <Box
          sx={{
            ...pageContentCardSx(theme, accent),
            p: { xs: 1.5, md: 2 },
            mb: 1.5,
          }}
        >
          <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
            {editingRateId
              ? t("CurrencyManagerPage.saveRate")
              : t("CurrencyManagerPage.addRate")}
          </Typography>
          {renderForm()}
        </Box>

        {/* Listado */}
        <Box sx={{ ...pageContentCardSx(theme, accent), p: { xs: 1.5, md: 0 } }}>
          <Box sx={{ px: { xs: 0, md: 2 }, pt: { xs: 0, md: 2 }, pb: { xs: 0, md: 1 } }}>
            <Typography sx={{ ...pageSectionTitleSx, mb: { xs: 1.25, md: 1 } }}>
              {t("CurrencyManagerPage.existingRates")}
              {!loading ? (
                <Typography
                  component="span"
                  sx={{
                    ml: 0.75,
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    color: "text.disabled",
                  }}
                >
                  ({filteredRates.length})
                </Typography>
              ) : null}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: { xs: 0, md: 2 }, pb: 2 }}>
              {isMobile ? (
                <Stack spacing={1.25}>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={108}
                      sx={{ borderRadius: 2.5 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
              )}
            </Box>
          ) : filteredRates.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                mx: { xs: 0, md: 2 },
                mb: 2,
                py: 4,
                px: 2,
                textAlign: "center",
                borderRadius: 2.5,
                borderStyle: "dashed",
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <CurrencyExchangeOutlinedIcon
                sx={{ fontSize: 36, color: "action.disabled", mb: 1 }}
              />
              <Typography color="text.secondary" fontWeight={600}>
                {search.trim()
                  ? t("CurrencyManagerPage.noSearchResults", {
                      defaultValue: "No hay tasas con esa búsqueda",
                    })
                  : t("CurrencyManagerPage.noRates", {
                      defaultValue: "Aún no hay tasas guardadas",
                    })}
              </Typography>
            </Paper>
          ) : isMobile ? (
            <Stack spacing={1.25} sx={{ pb: 1.5 }}>
              {filteredRates.map((rate) => renderMobileRateCard(rate))}
            </Stack>
          ) : (
            <Box
              sx={{
                width: "100%",
                minHeight: 380,
                px: 2,
                pb: 2,
              }}
            >
              <DataGrid
                rows={filteredRates}
                columns={gridColumns}
                getRowId={getRowId}
                autoHeight
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.id === String(editingRateId) ? "currency-row-editing" : ""
                }
                sx={{
                  ...pageDataGridSx(theme),
                  "& .currency-row-editing": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CurrencyManager;
